import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import GoogleCalendarConnect from "../components/GoogleCalendarConnect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, Plus, Loader2, ChevronLeft, ChevronRight,
  Video, Phone, Clock, User, Trash2, Edit2, Link2, Copy, Check,
  Settings, ExternalLink, MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DAY_KEYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: prevDays - i, current: false });
  for (let i = 1; i <= daysInMonth; i++) cells.push({ day: i, current: true });
  const rem = 42 - cells.length;
  for (let i = 1; i <= rem; i++) cells.push({ day: i, current: false });
  return cells;
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function CalendarPage({ user }) {
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);

  // Appointment form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", start_time: "", duration: "30",
    meeting_type: "phone_call", meeting_link: "", phone_number: "",
    lead_id: "", lead_name: "", contact_name: "", contact_email: "",
    assigned_to: "", assigned_to_name: ""
  });
  const [saving, setSaving] = useState(false);

  // Availability
  const [showAvailability, setShowAvailability] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [avForm, setAvForm] = useState({});
  const [savingAv, setSavingAv] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchAppointments();
    fetch(`${API}/users`, { credentials: "include" }).then(r => r.ok ? r.json() : []).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/leads?limit=200`, { credentials: "include" }).then(r => r.ok ? r.json() : {}).then(d => setLeads(d.leads || [])).catch(() => {});
    fetchAvailability();
  }, []);

  useEffect(() => { fetchAppointments(); }, [currentDate, view]);

  const fetchAppointments = async () => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    let start, end;
    if (view === "month") {
      start = new Date(y, m, 1).toISOString().slice(0, 10);
      end = new Date(y, m + 1, 0).toISOString().slice(0, 10);
    } else if (view === "week") {
      const d = currentDate.getDay();
      const ws = new Date(currentDate); ws.setDate(currentDate.getDate() - d);
      const we = new Date(ws); we.setDate(ws.getDate() + 6);
      start = ws.toISOString().slice(0, 10);
      end = we.toISOString().slice(0, 10);
    } else {
      start = currentDate.toISOString().slice(0, 10);
      end = start;
    }
    try {
      const res = await fetch(`${API}/appointments?start=${start}T00:00:00&end=${end}T23:59:59`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setAppointments(d.appointments || []); }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const fetchAvailability = async () => {
    try {
      const res = await fetch(`${API}/booking/availability`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setAvailability(d); setAvForm({ ...d }); }
    } catch (e) { /* silent */ }
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const openCreate = (dateStr) => {
    setEditingAppt(null);
    const dt = dateStr ? `${dateStr}T09:00` : new Date().toISOString().slice(0, 16);
    setForm({
      title: "", description: "", start_time: dt, duration: "30",
      meeting_type: "phone_call", meeting_link: "", phone_number: "",
      lead_id: "", lead_name: "", contact_name: "", contact_email: "",
      assigned_to: user?.user_id || "", assigned_to_name: user?.name || ""
    });
    setDialogOpen(true);
  };

  const openEdit = (appt) => {
    setEditingAppt(appt);
    setForm({
      ...appt,
      start_time: appt.start_time?.slice(0, 16) || "",
      duration: String(appt.duration || 30),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.start_time) { toast.error("Date/time is required"); return; }
    setSaving(true);
    try {
      const url = editingAppt
        ? `${API}/appointments/${editingAppt.appointment_id}`
        : `${API}/appointments`;
      const method = editingAppt ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ ...form, duration: parseInt(form.duration) })
      });
      if (res.ok) {
        toast.success(editingAppt ? "Appointment updated" : "Appointment created");
        setDialogOpen(false);
        fetchAppointments();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await fetch(`${API}/appointments/${id}`, { method: "DELETE", credentials: "include" });
      toast.success("Deleted");
      setDialogOpen(false);
      fetchAppointments();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleSaveAvailability = async () => {
    setSavingAv(true);
    try {
      const res = await fetch(`${API}/booking/availability`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(avForm)
      });
      if (res.ok) {
        toast.success("Availability saved");
        fetchAvailability();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (e) { toast.error("Failed to save"); }
    finally { setSavingAv(false); }
  };

  const bookingUrl = availability?.booking_slug
    ? `${window.location.origin}/api/book-page/${availability.booking_slug}`
    : "";

  const copyBookingLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    toast.success("Booking link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getApptsForDate = (dateStr) => appointments.filter(a => a.start_time?.startsWith(dateStr));

  const headerLabel = () => {
    if (view === "month") return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === "day") return currentDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const ws = new Date(currentDate); ws.setDate(currentDate.getDate() - currentDate.getDay());
    const we = new Date(ws); we.setDate(ws.getDate() + 6);
    return `${ws.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${we.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  // Week days for week view
  const weekDays = useMemo(() => {
    const ws = new Date(currentDate);
    ws.setDate(currentDate.getDate() - currentDate.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ws); d.setDate(ws.getDate() + i); return d;
    });
  }, [currentDate]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 7); // 7am-6pm

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-primary" />
            Calendar
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <GoogleCalendarConnect />
          {bookingUrl && (
            <Button variant="outline" size="sm" onClick={copyBookingLink} data-testid="copy-booking-link">
              {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? "Copied" : "Booking Link"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowAvailability(!showAvailability)} data-testid="availability-settings-btn">
            <Settings className="w-3.5 h-3.5 mr-1" /> Availability
          </Button>
          <Button size="sm" onClick={() => openCreate(null)} data-testid="create-appointment-btn">
            <Plus className="w-4 h-4 mr-1" /> New Appointment
          </Button>
        </div>
      </div>

      {/* Availability Settings Panel */}
      {showAvailability && (
        <Card data-testid="availability-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-4 h-4" /> Availability & Booking Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Booking Link Slug</Label>
                <div className="flex gap-1">
                  <span className="text-sm text-slate-400 self-center">/book/</span>
                  <Input value={avForm.booking_slug || ""} onChange={e => setAvForm({ ...avForm, booking_slug: e.target.value })} data-testid="booking-slug-input" />
                </div>
              </div>
              <div>
                <Label>Default Duration (min)</Label>
                <Select value={String(avForm.default_duration || 30)} onValueChange={v => setAvForm({ ...avForm, default_duration: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Buffer Between Meetings (min)</Label>
                <Select value={String(avForm.buffer_minutes || 15)} onValueChange={v => setAvForm({ ...avForm, buffer_minutes: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    <SelectItem value="5">5 min</SelectItem>
                    <SelectItem value="10">10 min</SelectItem>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Weekly Hours</Label>
              <div className="space-y-2">
                {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map(day => {
                  const cfg = (avForm.availability || {})[day] || { enabled: false, start: "09:00", end: "17:00" };
                  return (
                    <div key={day} className="flex items-center gap-3">
                      <Checkbox checked={cfg.enabled} onCheckedChange={checked => {
                        const av = { ...(avForm.availability || {}), [day]: { ...cfg, enabled: checked } };
                        setAvForm({ ...avForm, availability: av });
                      }} />
                      <span className="w-24 text-sm capitalize font-medium">{day}</span>
                      <Input type="time" value={cfg.start} className="w-28 h-8 text-sm" disabled={!cfg.enabled}
                        onChange={e => { const av = { ...(avForm.availability || {}), [day]: { ...cfg, start: e.target.value } }; setAvForm({ ...avForm, availability: av }); }} />
                      <span className="text-slate-400 text-sm">to</span>
                      <Input type="time" value={cfg.end} className="w-28 h-8 text-sm" disabled={!cfg.enabled}
                        onChange={e => { const av = { ...(avForm.availability || {}), [day]: { ...cfg, end: e.target.value } }; setAvForm({ ...avForm, availability: av }); }} />
                    </div>
                  );
                })}
              </div>
            </div>
            <Button onClick={handleSaveAvailability} disabled={savingAv} data-testid="save-availability-btn">
              {savingAv && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Availability
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Calendar Navigation */}
      <Card>
        <CardContent className="pt-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
              <Button variant="ghost" size="sm" onClick={() => navigate(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
              <h2 className="text-lg font-semibold text-slate-800 ml-2" data-testid="cal-header">{headerLabel()}</h2>
            </div>
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="h-8">
                <TabsTrigger value="month" className="text-xs px-3 h-7" data-testid="view-month">Month</TabsTrigger>
                <TabsTrigger value="week" className="text-xs px-3 h-7" data-testid="view-week">Week</TabsTrigger>
                <TabsTrigger value="day" className="text-xs px-3 h-7" data-testid="view-day">Day</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* MONTH VIEW */}
          {view === "month" && (
            <div data-testid="month-view">
              <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-t-lg overflow-hidden">
                {DAYS.map(d => <div key={d} className="bg-slate-50 py-2 text-center text-xs font-semibold text-slate-500">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-b-lg overflow-hidden">
                {getMonthDays(currentDate.getFullYear(), currentDate.getMonth()).map((cell, i) => {
                  const dateStr = cell.current
                    ? `${currentDate.getFullYear()}-${String(currentDate.getMonth()+1).padStart(2,"0")}-${String(cell.day).padStart(2,"0")}`
                    : "";
                  const dayAppts = dateStr ? getApptsForDate(dateStr) : [];
                  const isToday = cell.current && cell.day === new Date().getDate() && currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear();
                  return (
                    <div key={i}
                      className={`bg-white min-h-[80px] p-1 cursor-pointer hover:bg-blue-50/50 transition-colors ${!cell.current ? "bg-slate-50/50" : ""}`}
                      onClick={() => cell.current && openCreate(dateStr)}
                    >
                      <span className={`text-xs font-medium ${isToday ? "bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center" : cell.current ? "text-slate-700" : "text-slate-300"}`}>
                        {cell.day}
                      </span>
                      <div className="space-y-0.5 mt-0.5">
                        {dayAppts.slice(0, 3).map(a => (
                          <div key={a.appointment_id}
                            className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer ${a.meeting_type === "video_call" ? "bg-blue-100 text-blue-700" : a.meeting_type === "in_person" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}
                            onClick={e => { e.stopPropagation(); openEdit(a); }}
                          >
                            {formatTime(a.start_time)} {a.title}
                          </div>
                        ))}
                        {dayAppts.length > 3 && <span className="text-[10px] text-slate-400 px-1">+{dayAppts.length - 3} more</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* WEEK VIEW */}
          {view === "week" && (
            <div className="overflow-auto" data-testid="week-view">
              <div className="grid grid-cols-8 min-w-[800px]">
                <div className="border-r border-slate-200" />
                {weekDays.map((d, i) => {
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <div key={i} className={`text-center py-2 border-r border-slate-200 ${isToday ? "bg-blue-50" : ""}`}>
                      <p className="text-xs text-slate-500">{DAYS[d.getDay()]}</p>
                      <p className={`text-sm font-semibold ${isToday ? "text-primary" : "text-slate-700"}`}>{d.getDate()}</p>
                    </div>
                  );
                })}
                {hours.map(h => (
                  <div key={h} className="contents">
                    <div className="border-r border-b border-slate-100 pr-2 py-1 text-right text-[10px] text-slate-400">
                      {h > 12 ? h - 12 : h}{h >= 12 ? "pm" : "am"}
                    </div>
                    {weekDays.map((d, di) => {
                      const dateStr = d.toISOString().slice(0, 10);
                      const hourAppts = getApptsForDate(dateStr).filter(a => {
                        const ah = new Date(a.start_time).getHours();
                        return ah === h;
                      });
                      return (
                        <div key={di} className="border-r border-b border-slate-100 min-h-[40px] p-0.5 cursor-pointer hover:bg-blue-50/30"
                          onClick={() => openCreate(dateStr)}
                        >
                          {hourAppts.map(a => (
                            <div key={a.appointment_id}
                              className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer mb-0.5 ${a.meeting_type === "video_call" ? "bg-blue-100 text-blue-700" : a.meeting_type === "in_person" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}
                              onClick={e => { e.stopPropagation(); openEdit(a); }}
                            >
                              {formatTime(a.start_time)} {a.title}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DAY VIEW */}
          {view === "day" && (
            <div data-testid="day-view">
              {hours.map(h => {
                const dateStr = currentDate.toISOString().slice(0, 10);
                const hourAppts = getApptsForDate(dateStr).filter(a => new Date(a.start_time).getHours() === h);
                return (
                  <div key={h} className="flex border-b border-slate-100 min-h-[50px] cursor-pointer hover:bg-blue-50/30"
                    onClick={() => openCreate(dateStr)}
                  >
                    <div className="w-16 pr-2 py-2 text-right text-xs text-slate-400 border-r border-slate-100 flex-shrink-0">
                      {h > 12 ? h - 12 : h}:00 {h >= 12 ? "PM" : "AM"}
                    </div>
                    <div className="flex-1 p-1 space-y-1">
                      {hourAppts.map(a => (
                        <div key={a.appointment_id}
                          className={`px-3 py-2 rounded-lg cursor-pointer ${a.meeting_type === "video_call" ? "bg-blue-50 border border-blue-200" : a.meeting_type === "in_person" ? "bg-violet-50 border border-violet-200" : "bg-emerald-50 border border-emerald-200"}`}
                          onClick={e => { e.stopPropagation(); openEdit(a); }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{a.title}</p>
                              <p className="text-xs text-slate-500">{formatTime(a.start_time)} - {a.duration}min</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {a.meeting_type === "video_call" ? <Video className="w-4 h-4 text-blue-500" /> : a.meeting_type === "in_person" ? <MapPin className="w-4 h-4 text-violet-500" /> : <Phone className="w-4 h-4 text-emerald-500" />}
                              {a.contact_name && <span className="text-xs text-slate-500">{a.contact_name}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appointment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              {editingAppt ? "Edit Appointment" : "New Appointment"}
              {editingAppt && (
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-8"
                  onClick={() => handleDelete(editingAppt.appointment_id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Meeting title" data-testid="appt-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date & Time *</Label>
                <Input type="datetime-local" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="appt-datetime" />
              </div>
              <div>
                <Label>Duration</Label>
                <Select value={form.duration} onValueChange={v => setForm({ ...form, duration: v })}>
                  <SelectTrigger data-testid="appt-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Meeting Type</Label>
              <div className="flex gap-2 mt-1">
                <Button type="button" variant={form.meeting_type === "video_call" ? "default" : "outline"} size="sm"
                  onClick={() => setForm({ ...form, meeting_type: "video_call" })} data-testid="type-video">
                  <Video className="w-4 h-4 mr-1" /> Video Call
                </Button>
                <Button type="button" variant={form.meeting_type === "phone_call" ? "default" : "outline"} size="sm"
                  onClick={() => setForm({ ...form, meeting_type: "phone_call" })} data-testid="type-phone">
                  <Phone className="w-4 h-4 mr-1" /> Phone Call
                </Button>
                <Button type="button" variant={form.meeting_type === "in_person" ? "default" : "outline"} size="sm"
                  onClick={() => setForm({ ...form, meeting_type: "in_person" })} data-testid="type-in-person">
                  <MapPin className="w-4 h-4 mr-1" /> In Person
                </Button>
              </div>
            </div>
            {form.meeting_type === "video_call" && (
              <div>
                <Label>Meeting Link</Label>
                <Input value={form.meeting_link} onChange={e => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://zoom.us/j/..." data-testid="appt-link" />
              </div>
            )}
            {form.meeting_type === "phone_call" && (
              <div>
                <Label>Phone Number</Label>
                <Input value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} placeholder="(555) 123-4567" data-testid="appt-phone" />
              </div>
            )}
            {form.meeting_type === "in_person" && (
              <div>
                <Label>Meeting Location</Label>
                <Input value={form.meeting_location || ""} onChange={e => setForm({ ...form, meeting_location: e.target.value })} placeholder="123 Main St, Suite 200" data-testid="appt-location" />
              </div>
            )}
            <div>
              <Label>Description / Notes</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Notes..." rows={2} data-testid="appt-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contact Name</Label>
                <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" data-testid="appt-contact-name" />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="john@acme.com" data-testid="appt-contact-email" />
              </div>
            </div>
            <div>
              <Label>Link to Lead</Label>
              <Select value={form.lead_id || "_none"} onValueChange={v => {
                const l = leads.find(l => l.lead_id === v);
                setForm({ ...form, lead_id: v === "_none" ? "" : v, lead_name: l?.business_name || "" });
              }}>
                <SelectTrigger data-testid="appt-lead"><SelectValue placeholder="No lead linked" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No lead linked</SelectItem>
                  {leads.map(l => <SelectItem key={l.lead_id} value={l.lead_id}>{l.business_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned To</Label>
              <Select value={form.assigned_to || "_none"} onValueChange={v => {
                const u = users.find(u => u.user_id === v);
                setForm({ ...form, assigned_to: v === "_none" ? "" : v, assigned_to_name: u?.name || "" });
              }}>
                <SelectTrigger data-testid="appt-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Unassigned</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} disabled={saving} data-testid="save-appointment-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingAppt ? "Update" : "Create"} Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
